import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Building2, UserPlus, UserMinus, Crown, Shield, ShieldOff, Image as ImageIcon, Save, Upload, CreditCard, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Link } from 'react-router-dom';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'AED', 'SGD'];

const Organization = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('accountant');
  const [brandingDraft, setBrandingDraft] = useState({ name: '', display_currency: '' });
  const [logoUrl, setLogoUrl] = useState(null);
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    try {
      const [orgRes, membersRes, subRes] = await Promise.all([
        axios.get(`${API}/organizations/me`),
        axios.get(`${API}/organizations/members`),
        axios.get(`${API}/billing/subscription`).catch(() => ({ data: null })),
      ]);
      setOrg(orgRes.data);
      setMembers(membersRes.data);
      setSubscription(subRes.data);
      setBrandingDraft({
        name: orgRes.data?.name || '',
        display_currency: orgRes.data?.display_currency || '',
      });
      if (orgRes.data?.logo_file_id) {
        try {
          const u = await axios.post(`${API}/files/${orgRes.data.logo_file_id}/url`, null, { params: { ttl: 3600 } });
          setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}${u.data.url}`);
        } catch {
          setLogoUrl(null);
        }
      } else {
        setLogoUrl(null);
      }
    } catch (e) {
      toast.error('Failed to load organization');
    }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      const payload = {};
      if (brandingDraft.name && brandingDraft.name !== org?.name) payload.name = brandingDraft.name;
      if (brandingDraft.display_currency !== (org?.display_currency || '')) {
        payload.display_currency = brandingDraft.display_currency || null;
      }
      if (Object.keys(payload).length === 0) {
        toast.message('No changes to save');
        return;
      }
      await axios.put(`${API}/organizations/branding`, payload);
      toast.success('Branding updated');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update branding');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo too large. Max 2 MB');
      return;
    }
    const form = new FormData();
    form.append('file', file);
    setUploadingLogo(true);
    try {
      await axios.post(`${API}/organizations/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      toast.success('Logo updated');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/organizations/invite`, { email: inviteEmail, role: inviteRole });
      toast.success(`${inviteEmail} added`);
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('accountant');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/users/${memberId}/role`, { role: newRole });
      toast.success('Role updated');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemove = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from this organization? They'll keep their own personal organization.`)) return;
    try {
      await axios.delete(`${API}/organizations/members/${memberId}`);
      toast.success('Member removed');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  return (
    <div className="p-6 md:p-12" data-testid="organization-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Building2 size={32} className="text-slate-900" />
            <div>
              <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="organization-title">
                {org?.name || 'Organization'}
              </h1>
              <p className="text-slate-600 mt-1 font-ibm">{members.length} member{members.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {isAdmin && (
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="invite-member-button"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                >
                  <UserPlus size={16} className="mr-2" /> Invite Member
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white rounded-sm max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-chivo text-2xl">Invite Member</DialogTitle>
                  <DialogDescription className="text-slate-600 font-ibm text-sm">
                    The user must register or sign in via Google at least once. They&apos;ll be moved into this organization with the role you select.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleInvite} className="space-y-4" data-testid="invite-form">
                  <div>
                    <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Email</Label>
                    <Input
                      data-testid="invite-email-input"
                      type="email"
                      required
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Role</Label>
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger data-testid="invite-role-select" className="mt-2 bg-slate-50 border-slate-200 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="accountant">Accountant</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="submit"
                    data-testid="invite-submit-button"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  >
                    Send Invite
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {subscription && (
          <div
            className={`mb-6 rounded-sm border p-4 flex items-center justify-between gap-4 flex-wrap ${
              subscription.plan_tier === 'starter'
                ? 'border-amber-200 bg-amber-50'
                : 'border-emerald-200 bg-emerald-50/60'
            }`}
            data-testid="org-subscription-banner"
          >
            <div className="flex items-center gap-3">
              <CreditCard size={20} className={subscription.plan_tier === 'starter' ? 'text-amber-700' : 'text-emerald-700'} />
              <div>
                <div className="font-ibm font-semibold text-sm text-slate-900">
                  {subscription.plan?.name || subscription.plan_tier} plan
                  {subscription.plan_tier !== 'starter' && subscription.expires_at && (
                    <span className="text-slate-600 font-normal"> · renews {new Date(subscription.expires_at).toLocaleDateString()}</span>
                  )}
                </div>
                <p className="text-xs font-ibm text-slate-600 mt-0.5">
                  {subscription.plan_tier === 'starter'
                    ? 'Upgrade to unlock more transactions, webhooks, and API keys.'
                    : `Caps: ${subscription.plan?.caps?.transactions_per_month === -1 ? 'unlimited' : `${subscription.plan?.caps?.transactions_per_month?.toLocaleString()} txns`} / month, ${subscription.plan?.caps?.webhooks} webhooks, ${subscription.plan?.caps?.api_keys} API keys.`}
                </p>
              </div>
            </div>
            <Link to="/billing" data-testid="org-subscription-manage-button">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform">
                {subscription.plan_tier === 'starter' ? 'Upgrade' : 'Manage Billing'}
                <ArrowUpRight size={14} className="ml-2" />
              </Button>
            </Link>
          </div>
        )}

        {isAdmin && (
          <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="org-branding-card">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon size={18} className="text-slate-600" />
              <h2 className="text-lg font-chivo font-bold text-slate-900">Organization Branding</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-1 flex flex-col items-center justify-center">
                <div className="w-32 h-32 bg-slate-100 rounded-sm border border-slate-200 flex items-center justify-center overflow-hidden" data-testid="org-logo-preview">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Org logo" className="w-full h-full object-contain" data-testid="org-logo-img" />
                  ) : (
                    <ImageIcon size={36} className="text-slate-300" />
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleLogoUpload}
                  data-testid="org-logo-input"
                />
                <Button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="org-logo-upload-button"
                  className="mt-3 bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs hover:scale-[0.98] transition-transform"
                >
                  <Upload size={14} className="mr-2" /> {uploadingLogo ? 'Uploading…' : 'Upload Logo'}
                </Button>
                <p className="text-[10px] font-ibm text-slate-500 mt-2 text-center">PNG / JPG / WEBP, max 2 MB</p>
              </div>
              <div className="md:col-span-2 space-y-4">
                <div>
                  <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Display Name</Label>
                  <Input
                    data-testid="org-branding-name-input"
                    value={brandingDraft.name}
                    onChange={(e) => setBrandingDraft({ ...brandingDraft, name: e.target.value })}
                    className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                    Display Currency Override
                  </Label>
                  <Select
                    value={brandingDraft.display_currency || 'none'}
                    onValueChange={(v) =>
                      setBrandingDraft({ ...brandingDraft, display_currency: v === 'none' ? '' : v })
                    }
                  >
                    <SelectTrigger data-testid="org-branding-currency-select" className="mt-2 bg-slate-50 border-slate-200 rounded-sm">
                      <SelectValue placeholder="Use app default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Use app default</SelectItem>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs font-ibm text-slate-500 mt-1">
                    Overrides the app-level base currency for this organization&apos;s analytics and reports.
                  </p>
                </div>
                <Button
                  onClick={handleSaveBranding}
                  disabled={savingBranding}
                  data-testid="org-branding-save-button"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                >
                  <Save size={14} className="mr-2" /> {savingBranding ? 'Saving…' : 'Save Branding'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="members-table">
          <table className="w-full">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Member</th>
                <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Role</th>
                <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Auth</th>
                <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">2FA</th>
                {isAdmin && <th className="px-4 py-4 text-right text-xs font-ibm font-semibold uppercase tracking-widest">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((m, idx) => (
                <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`member-row-${idx}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {m.google_picture ? (
                        <img src={m.google_picture} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-ibm font-semibold text-slate-700">
                          {m.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                      <div>
                        <div className="font-ibm text-sm text-slate-900 flex items-center gap-1">
                          {m.name}
                          {m.is_owner && <Crown size={12} className="text-amber-500" title="Owner" />}
                        </div>
                        <div className="font-ibm text-xs text-slate-500">{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {isAdmin && m.id !== user?.id ? (
                      <Select value={m.role} onValueChange={(v) => handleRoleChange(m.id, v)}>
                        <SelectTrigger data-testid={`member-role-${idx}`} className="w-32 bg-white border-slate-200 rounded-sm h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="accountant">Accountant</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs font-ibm uppercase tracking-widest font-semibold text-slate-700">{m.role}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-ibm text-xs text-slate-600 capitalize">{m.auth_provider}</td>
                  <td className="px-4 py-3">
                    {m.twofa_enabled ? (
                      <Shield size={14} className="text-emerald-600" />
                    ) : (
                      <ShieldOff size={14} className="text-slate-300" />
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      {m.id !== user?.id && !m.is_owner && (
                        <button
                          onClick={() => handleRemove(m.id, m.email)}
                          data-testid={`member-remove-${idx}`}
                          className="p-2 hover:bg-red-50 rounded-sm transition-colors"
                          title="Remove from org"
                        >
                          <UserMinus size={14} className="text-red-600" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default Organization;
